import React, {FC, useState} from 'react'
import axios from 'axios'
import {API_URL} from '../../modules/auth/core/_requests'

// --- Interfaces to match the backend response ---
interface ScrapeError {
  variantName: string;
  error?: string;
}

interface ScrapeSummary {
  productUrl: string;
  productTitle: string;
  brandName: string;
  variantsFound: number;
  successfulVariantScrapes: number;
  failedVariantScrapes: number;
  newVariantsAdded: number;
  variantsUpdated: number;
  dbErrorMessage: string | null;
}

interface ScrapeResult {
  message: string;
  summary: ScrapeSummary;
  errors: ScrapeError[];
}

// --- Component Props ---
interface KangerWholesaleSingleProductModalProps {
  show: boolean;
  onClose: () => void;
  onScrapeComplete: () => void; // To trigger a refresh of the main product grid
}

export const KangerWholesaleSingleProductModal: FC<KangerWholesaleSingleProductModalProps> = ({
  show,
  onClose,
  onScrapeComplete,
}) => {
  const [url, setUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ScrapeResult | null>(null);

  const handleScrape = async () => {
    if (!url.trim() || !url.startsWith('https://www.kangerwholesaleusa.com')) {
      setError('Please enter a valid KangerWholesaleUSA product URL.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await axios.post(`${API_URL}/api/kangerwholesale/scrape-product`, { url });
      setResult(response.data);
      onScrapeComplete(); // Notify parent to refresh data
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'An unknown error occurred.';
      setError(errorMessage);
      console.error('Scrape error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Reset state on close
    setUrl('');
    setIsLoading(false);
    setError(null);
    setResult(null);
    onClose();
  };

  if (!show) {
    return null;
  }

  return (
    <div className='modal show d-block' tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
      <div className='modal-dialog modal-lg modal-dialog-centered'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>
              <i className='fas fa-search-plus me-2'></i>
              Scrape Single Product
            </h5>
            <button type='button' className='btn-close' onClick={handleClose}></button>
          </div>
          <div className='modal-body'>
            {/* --- Input Section --- */}
            <div className='mb-4'>
              <label htmlFor='product-url-input' className='form-label fw-semibold'>
                Product URL
              </label>
              <div className='input-group'>
                <input
                  type='text'
                  id='product-url-input'
                  className='form-control'
                  placeholder='https://www.kangerwholesaleusa.com/product/...'
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  disabled={isLoading}
                />
                <button
                  className='btn btn-primary'
                  onClick={handleScrape}
                  disabled={isLoading || !url.trim()}
                >
                  {isLoading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' role='status'></span>
                      Scraping...
                    </>
                  ) : (
                    <>
                      <i className='fas fa-cogs me-2'></i>
                      Scrape
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* --- Error Display --- */}
            {error && <div className='alert alert-danger'>{error}</div>}

            {/* --- Result Display --- */}
            {result && (
              <div className='mt-4 animate__animated animate__fadeIn'>
                <h4 className='mb-3'>Scrape Result</h4>
                <div className='card'>
                  <div className='card-header'>
                    <h5 className='card-title mb-0'>{result.summary.productTitle}</h5>
                    <small className='text-muted'>{result.summary.brandName}</small>
                  </div>
                  <div className='card-body'>
                    <div className='row'>
                      {/* Scrape Stats */}
                      <div className='col-md-6'>
                        <h6>
                          <i className='fas fa-chart-bar me-2 text-primary'></i>Scrape Statistics
                        </h6>
                        <ul className='list-group list-group-flush'>
                          <li className='list-group-item d-flex justify-content-between align-items-center bg-dark text-light'>
                            Variants Found
                            <span className='badge bg-info'>{result.summary.variantsFound}</span>
                          </li>
                          <li className='list-group-item d-flex justify-content-between align-items-center bg-dark text-light'>
                            Successful Scrapes
                            <span className='badge bg-success'>
                              {result.summary.successfulVariantScrapes}
                            </span>
                          </li>
                          <li className='list-group-item d-flex justify-content-between align-items-center bg-dark text-light'>
                            Failed Scrapes
                            <span className='badge bg-danger'>{result.summary.failedVariantScrapes}</span>
                          </li>
                        </ul>
                      </div>
                      {/* Database Stats */}
                      <div className='col-md-6'>
                        <h6>
                          <i className='fas fa-database me-2 text-success'></i>Database Operations
                        </h6>
                        <ul className='list-group list-group-flush'>
                          <li className='list-group-item d-flex justify-content-between align-items-center bg-dark text-light'>
                            New Variants Added
                            <span className='badge bg-primary'>{result.summary.newVariantsAdded}</span>
                          </li>
                          <li className='list-group-item d-flex justify-content-between align-items-center bg-dark text-light'>
                            Existing Variants Updated
                            <span className='badge bg-warning'>{result.summary.variantsUpdated}</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    {/* Errors Section */}
                    {result.errors && result.errors.length > 0 && (
                      <div className='mt-4'>
                        <h6>
                          <i className='fas fa-exclamation-triangle me-2 text-danger'></i>Encountered Errors
                        </h6>
                        <div className='accordion' id='error-accordion'>
                          <div className='accordion-item'>
                            <h2 className='accordion-header' id='error-heading'>
                              <button
                                className='accordion-button collapsed'
                                type='button'
                                data-bs-toggle='collapse'
                                data-bs-target='#error-collapse'
                                aria-expanded='false'
                                aria-controls='error-collapse'
                              >
                                View {result.errors.length} Error(s)
                              </button>
                            </h2>
                            <div
                              id='error-collapse'
                              className='accordion-collapse collapse'
                              aria-labelledby='error-heading'
                              data-bs-parent='#error-accordion'
                            >
                              <div className='accordion-body' style={{maxHeight: '200px', overflowY: 'auto'}}>
                                <ul className='list-group'>
                                  {result.errors.map((e, index) => (
                                    <li key={index} className='list-group-item list-group-item-danger'>
                                      <strong>{e.variantName}:</strong> {e.error}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                   <div className='card-footer text-center'>
                      <a href={result.summary.productUrl} target='_blank' rel='noopener noreferrer' className='btn btn-sm btn-outline-secondary'>
                        View Original Product Page
                        <i className='fas fa-external-link-alt ms-2'></i>
                      </a>
                    </div>
                </div>
              </div>
            )}
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-secondary' onClick={handleClose}>
              Close
            </button>
             <button
              type='button'
              className='btn btn-info'
              onClick={() => {
                setResult(null);
                setError(null);
                setUrl('');
              }}
              disabled={isLoading || (!result && !error)}
            >
              <i className='fas fa-sync-alt me-2'></i>
              Scrape Another
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
